// Pending Matches (PD-13891) filters — Search / State / Type(source), plus the
// PD-14449 compliance-officer set (Customer/Lead, confidence, new-vs-linked).
// These render the filter tray on the consolidated match-review list
// (listKey 'license_match'). The backend handler is the custom
// GET /api/licenses/matches/list endpoint, which reads these query params from
// the filters JSON (matchKeywords / matchState / matchSource /
// matchLeadOrCustomer / matchConfidence / matchNewLeadsOnly), so no shared
// queryMapping is needed here.

import { createKeywordFilter } from './factories';
import type { FilterRegistry } from './types';

export const licenseMatchFilters: FilterRegistry = {
	matchKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search matches...',
			objectTable: 'client_license_match',
			listKeys: ['license_match'],
		}),
		queryParamName: 'matchKeywords',
	},

	matchState: {
		type: 'multi-select',
		label: 'State',
		placeholder: 'Select state...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/state/options',
		queryParamName: 'matchState',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		format: 'array',
		listKeys: ['license_match'],
	},

	matchSource: {
		type: 'multi-select',
		label: 'Type',
		placeholder: 'Select type...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchSource',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		// Normalized registry source classifications (lm_registry.source_type),
		// denormalized onto client_license_match.registry_source_type.
		options: [
			{ id: 'STATE_BOARD', label: 'State Board' },
			{ id: 'DEA', label: 'DEA' },
			{ id: 'NPI', label: 'NPI' },
			{ id: 'CONTROLLED_SUBSTANCE', label: 'Controlled Substance' },
			{ id: 'TOBACCO', label: 'Tobacco' },
			{ id: 'LIQUOR', label: 'Liquor' },
			{ id: 'OTHER', label: 'Other' },
		],
		format: 'array',
		listKeys: ['license_match'],
	},

	// PD-14449: the three filters below were ALREADY wired end-to-end in the
	// backend (licenses.controller.ts listPendingMatches maps them; the service
	// applies the WHERE clauses) but never exposed as UI controls.

	// Customer vs Lead — mirrors client.ts leadOrCustomer (the customer ask).
	// The controller only applies exactly 'Lead' / 'Customer'; 'All' means no filter.
	matchLeadOrCustomer: {
		type: 'radio',
		label: 'Lead/Customer Status',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchLeadOrCustomer',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'Lead', label: 'Leads' },
			{ id: 'Customer', label: 'Customers' },
		],
		defaultValue: { id: 'All', label: 'All' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},

	// Minimum confidence — lets an officer work the "ready to confirm" queue
	// (mirrors the 90%+ stat card). Controller: Number(value) -> confidence_score >=;
	// 'All' is NaN -> no filter.
	matchConfidence: {
		type: 'radio',
		label: 'Min Confidence',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchConfidence',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: '90', label: '90%+' },
			{ id: '80', label: '80%+' },
			{ id: '70', label: '70%+' },
		],
		defaultValue: { id: 'All', label: 'All' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},

	// New-license matches only (no existing client_license to link to) —
	// separates "brand-new license" review from "re-link to existing" review.
	// Controller accepts 'yes' (or boolean true); 'All' -> no filter.
	matchNewLeadsOnly: {
		type: 'radio',
		label: 'License Linkage',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchNewLeadsOnly',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'yes', label: 'New licenses only' },
		],
		defaultValue: { id: 'All', label: 'All' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},
};
